import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mergePricingResponse } from '../editor-rules';
import type { VariantPricing, VariantPricingUpdate } from '../types';
import { usePricingApi } from './use-pricing-api';
import { QK_VARIANT_PRICING } from './use-variant-pricing';

// Per-list pending state comes for free: one hook instance per price list card gives an
// `isPending` per card. The PATCH response is the authoritative pricing for the lists it
// touched, so it is merged into the query rather than invalidated up front.
export function useUpdateVariantPricing(sku: string | null | undefined) {
  const { pricingApi } = usePricingApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: VariantPricingUpdate[]) => {
      if (!sku) {
        throw new Error('SKU is required');
      }
      return pricingApi.updateVariantPricing(sku, updates);
    },
    onSuccess: (response) => {
      queryClient.setQueryData<VariantPricing[]>(QK_VARIANT_PRICING(sku), (current) =>
        mergePricingResponse(current, response),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QK_VARIANT_PRICING(sku) });
    },
  });
}
